// A smooth curve through a path's points (#231), three.js-free so the sim
// can place grind lines on exactly the curve a mesh draws. It's three's
// `CatmullRomCurve3` ("centripetal", open or closed) and `Curve.getPointAt` /
// `getTangentAt`, step for step, so `render/scenery/kit.ts`'s `samplePath`
// gets the same numbers through it (`curve.test.ts` compares the two).

/** A point in the world (m). */
export interface Point3 {
  x: number;
  z: number;
  y: number;
}

/** A sample along a curve: where it is, and the way it runs there (unit length). */
export interface CurveSample {
  at: Point3;
  dir: Point3;
}

/** Three's default arc-length table size (`Curve.arcLengthDivisions`). */
const ARC_DIVISIONS = 200;

/** Three's `CubicPoly.initNonuniformCatmullRom` then `calc`, for one axis. */
function cubic(x0: number, x1: number, x2: number, x3: number, dt0: number, dt1: number, dt2: number, t: number): number {
  let t1 = (x1 - x0) / dt0 - (x2 - x0) / (dt0 + dt1) + (x2 - x1) / dt1;
  let t2 = (x2 - x1) / dt1 - (x3 - x1) / (dt1 + dt2) + (x3 - x2) / dt2;
  t1 *= dt1;
  t2 *= dt1;
  const c0 = x1;
  const c1 = t1;
  const c2 = -3 * x1 + 3 * x2 - 2 * t1 - t2;
  const c3 = 2 * x1 - 2 * x2 + t1 + t2;
  const tt = t * t;
  return c0 + c1 * t + c2 * tt + c3 * tt * t;
}

const dist2 = (a: Point3, b: Point3): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
};

/**
 * The point `t` (0 to 1, by point index, not distance) along a centripetal
 * Catmull-Rom through `points`; a closed one runs on from the last point
 * back round to the first.
 */
function pointOn(points: readonly Point3[], closed: boolean, t: number): Point3 {
  const l = points.length;
  const p = (l - (closed ? 0 : 1)) * t;
  let i = Math.floor(p);
  let w = p - i;
  if (closed) i += i > 0 ? 0 : (Math.floor(Math.abs(i) / l) + 1) * l;
  else if (w === 0 && i === l - 1) {
    i = l - 2;
    w = 1;
  }
  const p1 = points[i % l]!;
  const p2 = points[(i + 1) % l]!;
  // Past either end of an open one, the end point mirrored.
  const first = points[0]!;
  const second = points[1]!;
  const p0 = closed || i > 0 ? points[(i - 1) % l]! : { x: first.x - second.x + first.x, y: first.y - second.y + first.y, z: first.z - second.z + first.z };
  const last = points[l - 1]!;
  const before = points[l - 2]!;
  const p3 = closed || i + 2 < l ? points[(i + 2) % l]! : { x: last.x - before.x + last.x, y: last.y - before.y + last.y, z: last.z - before.z + last.z };
  let dt0 = Math.pow(dist2(p0, p1), 0.25);
  let dt1 = Math.pow(dist2(p1, p2), 0.25);
  let dt2 = Math.pow(dist2(p2, p3), 0.25);
  // Repeated points.
  if (dt1 < 1e-4) dt1 = 1;
  if (dt0 < 1e-4) dt0 = dt1;
  if (dt2 < 1e-4) dt2 = dt1;
  return {
    x: cubic(p0.x, p1.x, p2.x, p3.x, dt0, dt1, dt2, w),
    y: cubic(p0.y, p1.y, p2.y, p3.y, dt0, dt1, dt2, w),
    z: cubic(p0.z, p1.z, p2.z, p3.z, dt0, dt1, dt2, w),
  };
}

/** Three's `Vector3.normalize`. */
function unit(v: Point3): Point3 {
  const k = 1 / (Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1);
  return { x: v.x * k, y: v.y * k, z: v.z * k };
}

/**
 * Points along a path every `step` metres or a little less (evenly spaced, at
 * least 2 spans), through a smooth curve, with the way it runs at each:
 * three's `getPointAt` / `getTangentAt` at `i / n`. A `closed` curve goes
 * on from the last point back to the first (don't repeat it at the end), and
 * its last sample is its first again.
 */
export function sampleCurve(points: readonly Point3[], step: number, closed = false): CurveSample[] {
  const lengths = [0];
  let last = pointOn(points, closed, 0);
  let sum = 0;
  for (let p = 1; p <= ARC_DIVISIONS; p++) {
    const current = pointOn(points, closed, p / ARC_DIVISIONS);
    sum += Math.sqrt(dist2(current, last));
    lengths.push(sum);
    last = current;
  }
  // Three's `getUtoTmapping`: from a share of the length to `t`.
  const tOf = (u: number): number => {
    const il = lengths.length;
    const target = u * lengths[il - 1]!;
    let low = 0;
    let high = il - 1;
    while (low <= high) {
      const i = Math.floor(low + (high - low) / 2);
      const c = lengths[i]! - target;
      if (c < 0) low = i + 1;
      else if (c > 0) high = i - 1;
      else {
        high = i;
        break;
      }
    }
    const i = high;
    if (lengths[i] === target) return i / (il - 1);
    return (i + (target - lengths[i]!) / (lengths[i + 1]! - lengths[i]!)) / (il - 1);
  };
  const n = Math.max(2, Math.ceil(sum / step));
  const out: CurveSample[] = [];
  for (let k = 0; k <= n; k++) {
    const t = tOf(k / n);
    const a = pointOn(points, closed, Math.max(0, t - 0.0001));
    const b = pointOn(points, closed, Math.min(1, t + 0.0001));
    out.push({ at: pointOn(points, closed, t), dir: unit({ x: b.x - a.x, y: b.y - a.y, z: b.z - a.z }) });
  }
  return out;
}

/**
 * The level sideways direction (to the right) of travel along `dir`: three's
 * `dir x up`, normalized. Where the curve runs straight up or down it keeps
 * `prev`, and it never flips against `prev`. (`render/scenery/kit.ts`'s
 * `sideOf` is the same on Vector3s.)
 */
export function sideways(dir: Point3, prev?: Point3): Point3 {
  // dir x (0, 1, 0)
  const x = -dir.z;
  const z = dir.x;
  if (x * x + z * z < 1e-4) return prev ? { ...prev } : { x: 1, y: 0, z: 0 };
  const side = unit({ x, y: 0, z });
  if (prev && side.x * prev.x + side.z * prev.z < 0) return { x: -side.x, y: 0, z: -side.z };
  return side;
}
